/**
 * Phase 5 — the explanation (SPEC §2.3, §5.2; DESIGN.md §3.6).
 *
 * The same surface as `RevealScreen`, because to the player it *is* the same
 * screen: the ranking they were just shown stays where it was, the rating bar
 * stays where it was, and End turn is already under their thumb. The server's
 * phase change is the only thing that moves, and the point of this file is that
 * nobody can see it happen. Only the rows stop settling, having settled.
 *
 * The order comes off the `justification` frame, never off `reveal`: the store
 * nulls `reveal` as `phase_changed` goes past, and a phone that reconnects
 * mid-argument never had one. That is the case the server field exists for, and
 * it is exactly the phone whose owner is about to ask why they are below Priya.
 * If the frame carries no ranking, no ranking is drawn — a half-remembered list
 * would have the room arguing with the wrong order.
 *
 * The attribution chips and the score line are the reveal's own payload, still
 * held for this turn (`turnMemory`). A reconnected phone gets the order without
 * them rather than a reconstruction.
 */
import { useMemo, useState } from 'react';
import { Screen, WaitingFor } from '../../components/index.js';
import {
  RevealBoard,
  RevealFooter,
  RevealHeading,
  recallReveal,
  toLicenseMap,
  useTurnActions,
} from '../../components/reveal/index.js';
import {
  useHostControls,
  useIsHost,
  useJustification,
  useJustificationDone,
  usePlayers,
  useReveal,
  useSelfId,
  useThumbsVote,
  useTurn,
} from '../../store/index.js';

export function JustificationScreen() {
  const justification = useJustification();
  const live = useReveal();
  const turn = useTurn();
  const players = usePlayers();
  const selfId = useSelfId();
  const isHost = useIsHost();
  const { forceAdvance } = useHostControls();
  const sendEndTurn = useJustificationDone();
  const sendVote = useThumbsVote();
  const [advanced, setAdvanced] = useState(false);

  const turnIndex = turn?.index ?? -1;
  const questionNumber = justification?.question?.number ?? -1;

  const actions = useTurnActions({
    turnIndex,
    questionNumber,
    live: true,
    sendVote,
    sendEndTurn,
  });

  const licensePlayers = useMemo(() => toLicenseMap(players), [players]);
  // Whatever this phone still holds for *this* turn: usually the memory, since
  // the store has already dropped `reveal` by now. Either way it is the payload
  // the server sent, and a stale one from another turn is not used at all.
  const revealed = useMemo(() => {
    const matches =
      live?.turnIndex === turnIndex && live?.question.number === questionNumber ? live : null;
    return matches ?? recallReveal(turnIndex, questionNumber);
  }, [live, turnIndex, questionNumber]);

  const ranking = justification?.ranking?.length ? justification.ranking : null;
  const turnPlayerName =
    players.find((p) => p.id === justification?.turnPlayerId)?.name ?? 'They';
  const isTurnPlayer = selfId !== null && justification?.turnPlayerId === selfId;

  if (!selfId) {
    return (
      <Screen testId="justification-screen" eyebrow="Reveal">
        <WaitingFor headline="Counting the room." />
      </Screen>
    );
  }

  return (
    <Screen
      testId="justification-screen"
      eyebrow={questionNumber > 0 ? `Reveal · ${questionNumber}` : 'Reveal'}
      meta={isTurnPlayer ? 'YOUR TURN' : null}
      title={
        <RevealHeading
          question={justification?.question ?? null}
          isTurnPlayer={isTurnPlayer}
          turnPlayerName={turnPlayerName}
        />
      }
      bodyClassName="overflow-y-auto"
      footer={
        <RevealFooter
          turnPlayerName={turnPlayerName}
          isTurnPlayer={isTurnPlayer}
          isHost={isHost}
          delta={revealed?.deltas.find((d) => d.playerId === selfId) ?? null}
          vote={actions.vote}
          voted={justification?.selfVoted ?? false}
          onVote={actions.castVote}
          endRequested={actions.endRequested}
          onEndTurn={actions.endTurn}
          advanced={advanced}
          onForceAdvance={() => {
            setAdvanced(true);
            forceAdvance();
          }}
        />
      }
    >
      {ranking && (
        <RevealBoard
          ranking={ranking}
          players={licensePlayers}
          selfId={selfId}
          attribution={revealed?.attribution}
        />
      )}
    </Screen>
  );
}
